import type { EventInput } from '@fullcalendar/react';
import { formatIsoDate, parseIsoDate, subtractDay } from '@src/shared/lib';
import {
  formatDurationLabel,
  getAbsencesIdsForRange,
  getEventLabel,
  getUserAbsences,
} from '../lib/absence';
import type { AbsenceBlock } from '../lib/blocks';
import { getSlotFreeDates } from '../lib/calendar';
import { getUserWorktimeForDate, userResourceId } from '../lib/resources';
import type { Absence, ResourceUser, TimelineSlot } from '../types';

/**
 * Building the objects the renderer draws.
 *
 * One direction only: domain records in, `EventInput` out. Nothing here is read
 * back -- what the user does with an event returns through `gestures.ts` as a
 * slot -- so this is the whole of what the application says about how a day
 * should look.
 */

function getSelectedAbsenceDuration(
  slot: TimelineSlot | null,
  absence: Absence,
) {
  const activeAbsenceId =
    slot?.editedAbsenceId ??
    (slot?.selectedIds?.length === 1 ? slot.selectedIds[0] : null);

  if (
    !slot ||
    !slot.start ||
    !slot.end ||
    activeAbsenceId == null ||
    activeAbsenceId !== absence.id
  ) {
    return null;
  }
  if (!slot.startTime && !slot.endTime) {
    return null;
  }

  return {
    allDay: false,
    ...(slot.startTime && {
      start: formatIsoDate(slot.start, slot.startTime),
    }),
    ...(slot.endTime && { end: formatIsoDate(slot.start, slot.endTime) }),
  };
}

export function absencesToEvents(
  slot: TimelineSlot | null,
  absences: Absence[],
  dayView: boolean = false,
  blocks: readonly AbsenceBlock[] = [],
): EventInput[] {
  if (!absences?.length) {
    return [];
  }

  function eventDuration(absence: Absence) {
    let duration = {
      start: formatIsoDate(absence.date),
      end: formatIsoDate(absence.date),
      allDay: absence.allDay,
    };
    if (absence.startTime) {
      duration = {
        ...duration,
        start: formatIsoDate(absence.date, absence.startTime),
      };
    }
    if (absence.endTime) {
      duration = {
        ...duration,
        end: formatIsoDate(absence.date, absence.endTime),
      };
    }
    const slotDuration = getSelectedAbsenceDuration(slot, absence);
    if (slotDuration) {
      duration = {
        ...duration,
        ...slotDuration,
      };
    }
    return duration;
  }

  function getRenderEventId(absence: Absence) {
    const viewPrefix = dayView ? 'day' : 'month';
    const neutralSuffix = absence.useNeutralColour ? '-neutral' : '';
    const typeSuffix = absence.typeId != null ? `-t${absence.typeId}` : '';

    return `${viewPrefix}-${absence.id}${neutralSuffix}${typeSuffix}`;
  }

  // Day view draws each day on its own, so a day inside a multi-day block has
  // to say which of its edges the block carries on past.
  const allDayBlockByMember = new Map<number, AbsenceBlock>();
  if (dayView) {
    for (const block of blocks) {
      if (!block.allDay) continue;
      for (const id of block.memberIds) allDayBlockByMember.set(id, block);
    }
  }

  const dailyEvents = absences.map((a) => {
    const slotDuration = getSelectedAbsenceDuration(slot, a);
    const block =
      a.allDay && !slotDuration ? allDayBlockByMember.get(a.id) : undefined;
    const effectiveStartTime = slotDuration ? slot?.startTime : a.startTime;
    const effectiveEndTime = slotDuration ? slot?.endTime : a.endTime;
    const label =
      dayView && !a.allDay && effectiveStartTime && effectiveEndTime
        ? formatDurationLabel(effectiveStartTime, effectiveEndTime)
        : null;

    return {
      id: getRenderEventId(a),
      absenceId: a.id,
      resourceId: userResourceId(a.userId),
      userId: a.userId,
      type: a.typeId,
      neutralColour: a.useNeutralColour ?? false,
      approvedManager: a.approvedManager,
      approvedHr: a.approvedHr,
      tentative: a.absenceTentative ?? false,
      editable: false,
      colourFg: a.colourFg,
      colourBg: a.colourBg,
      ...eventDuration(a),
      ...(a.allDay && !slotDuration && { end: nextDay(a.dateStr) }),
      ...(dayView && {
        resourceEditable: false,
        startEditable: false,
        durationEditable: true,
        extendedProps: {
          ...(label && { label }),
          continuesBefore: !!block && block.dateFrom < a.dateStr,
          continuesAfter: !!block && block.dateTo > a.dateStr,
        },
      }),
    };
  });
  if (dayView) return dailyEvents;

  const eventsById = new Map(
    absences.map((absence, index) => [absence.id, dailyEvents[index]]),
  );
  const recordsById = new Map(absences.map((absence) => [absence.id, absence]));
  const remaining = new Set(recordsById.keys());
  const result: EventInput[] = [];
  for (const block of blocks) {
    if (!block.allDay) continue;
    // Membership comes from the projection. Only split for missing/hidden days;
    // never join separate blocks based on a client-side type comparison.
    const members = block.memberIds
      .map((id) => recordsById.get(id))
      .filter(
        (absence): absence is Absence =>
          !!absence && remaining.has(absence.id) && absence.allDay,
      )
      .sort((a, b) => a.dateStr.localeCompare(b.dateStr));
    for (const run of consecutiveRuns(members, (member) => member.dateStr)) {
      const first = run[0];
      const last = run[run.length - 1];
      result.push({
        ...eventsById.get(first.id),
        id: `block-${block.anchorId}-${eventsById.get(first.id)!.id}`,
        start: first.dateStr,
        end: nextDay(last.dateStr),
        allDay: true,
        blockAnchorId: block.anchorId,
        memberIds: run.map((member) => member.id),
        memberDates: Object.fromEntries(
          run.map((member) => [member.dateStr, member.id]),
        ),
        workdayCount: block.workdayCount,
      });
      for (const member of run) remaining.delete(member.id);
    }
  }
  for (const absence of absences) {
    if (remaining.has(absence.id)) result.push(eventsById.get(absence.id)!);
  }
  return result;
}

function nextDay(date: string): string {
  return formatIsoDate(subtractDay(parseIsoDate(date)!, -1));
}

/** Presentation gaps must remain gaps, even inside one server block or draft. */
export function consecutiveRuns<T>(
  items: readonly T[],
  dateOf: (item: T) => string,
): T[][] {
  const runs: T[][] = [];
  for (const item of items) {
    const run = runs[runs.length - 1];
    if (run && nextDay(dateOf(run[run.length - 1])) === dateOf(item))
      run.push(item);
    else runs.push([item]);
  }
  return runs;
}

export function getTransientEventsForSlot(
  slot: TimelineSlot,
  absences: Array<Absence>,
  users: Array<ResourceUser>,
  dayView = false,
) {
  if (!slot || !slot.start || !slot.end || !slot.userId) {
    return [];
  }
  const userAbsences = getUserAbsences(slot.userId, absences);
  if (dayView) {
    if (slot.editedAbsenceId) {
      // absencesToEvents reshapes the saved event to the draft hours/date.
      // A transient here would draw that edit twice, even after moving it
      // off its original date. This is a rendering guard, not overlap validation.
      //
      // Month view needs no such guard: getSelectedAbsenceDuration returns null
      // when the slot carries no times, so the saved event stays on its own
      // dates and the transients below only ever cover days it has left.
      return [];
    }
    const overlappingIds = getAbsencesIdsForRange(
      slot.start,
      slot.end,
      userAbsences,
      {
        dayView: true,
        startTime: slot.startTime,
        endTime: slot.endTime,
      },
    );
    if (overlappingIds.length > 0) {
      return [];
    }

    const userWorktime = getUserWorktimeForDate(slot.userId, users, slot.start);
    const startTime = slot.startTime || userWorktime?.start;
    const endTime = slot.endTime || userWorktime?.end;
    const label =
      startTime && endTime
        ? formatDurationLabel(startTime, endTime)
        : getEventLabel(slot.start, slot.end, true);
    return [
      {
        id: `transient-event-${slot.start}`,
        resourceId: slot.resourceId,
        start: formatIsoDate(slot.start, startTime),
        end: formatIsoDate(slot.end, endTime),
        className: 'transient-event',
        extendedProps: { label: label },
        startEditable: false,
        durationEditable: false,
        resourceEditable: false,
      },
    ];
  }

  const slotFreeDates = getSlotFreeDates(slot, userAbsences);
  if (!slotFreeDates.length) {
    return [];
  }
  // One event per day, not per run. A run carries a single label, and the
  // month-view label *is* the day number -- `getEventLabel`'s `!dayView` branch
  // returns `getDate()` -- so an aggregated draft can only ever print the start
  // day's number once. Per-day events put a number back in every cell, which is
  // what the selection preview is for. Runs still break at occupied days by
  // construction: `getSlotFreeDates` has already dropped them.
  return slotFreeDates.map((day) => {
    const date = formatIsoDate(day);
    return {
      id: `transient-event-${slot.resourceId}-${date}`,
      resourceId: slot.resourceId,
      start: date,
      // Exclusive, as v7 all-day events need -- the same shape
      // `absencesToEvents` gives a saved all-day record above.
      end: nextDay(date),
      allDay: true,
      className: 'transient-event transient-month-event',
      extendedProps: { label: getEventLabel(day, day, false) },
      editable: false,
    };
  });
}
